import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Dice5, Loader2, Hourglass, Lock as LockIcon, Users as UsersIcon, Timer } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useSessionSocket } from "@/hooks/useSessionSocket";
import { useCountdown } from "@/hooks/useCountdown";
import { EMOJI_CHOICES } from "@/lib/emojis";

const STORAGE_KEY = "rr_student_state";

const loadPersist = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};
const savePersist = (v) => localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
const clearPersist = () => localStorage.removeItem(STORAGE_KEY);

export default function StudentJoin() {
  const [searchParams] = useSearchParams();
  const codeFromUrl = (searchParams.get("code") || "").toUpperCase();

  const [code, setCode] = useState(codeFromUrl);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState(EMOJI_CHOICES[Math.floor(Math.random() * EMOJI_CHOICES.length)]);
  const [state, setState] = useState("form"); // 'form' | 'waiting' | 'revealed' | 'closed'
  const [studentId, setStudentId] = useState(null);
  const [myNumber, setMyNumber] = useState(null);
  const [myGroup, setMyGroup] = useState(null);
  const [myEmoji, setMyEmoji] = useState(null);
  const [myName, setMyName] = useState("");
  const [drawMode, setDrawMode] = useState("numbers");
  const [groupNames, setGroupNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalStudents, setTotalStudents] = useState(0);
  const [deadline, setDeadline] = useState(null);
  const countdown = useCountdown(deadline);

  const fetchSession = useCallback(async (c) => {
    if (!c) return;
    try {
      const { data } = await api.get(`/public/session/${c}`);
      setSessionInfo(data);
      setDeadline(data.submissions_deadline || null);
      if (data.status === "closed") setState((s) => (s === "form" ? "closed" : s));
    } catch (err) {
      console.warn("[student] session not found", err);
      setSessionInfo(null);
    }
  }, []);

  useEffect(() => {
    const p = loadPersist();
    if (p && p.code === code && p.studentId) {
      setStudentId(p.studentId);
      setMyName(p.name);
      setMyEmoji(p.emoji || null);
      if (p.number != null) {
        setMyNumber(p.number);
        setState("revealed");
      } else if (p.group != null) {
        setMyGroup(p.group);
        setDrawMode("groups");
        setGroupNames(p.groupNames || []);
        setState("revealed");
      } else {
        setState("waiting");
      }
    }
  }, [code]);

  useEffect(() => {
    fetchSession(code);
  }, [code, fetchSession]);

  useEffect(() => {
    const check = async () => {
      if (!studentId || !code) return;
      try {
        const { data } = await api.get(`/public/session/${code}/student/${studentId}`);
        const s = data.student;
        if (s.number != null || s.group != null) {
          setMyNumber(s.number);
          setMyGroup(s.group);
          setDrawMode(data.draw_mode);
          setGroupNames(data.group_names || []);
          setState("revealed");
          savePersist({
            code, studentId, name: myName, emoji: myEmoji,
            number: s.number, group: s.group, groupNames: data.group_names || [],
          });
        }
      } catch (err) {
        console.warn("[student] failed to hydrate student state", err);
      }
    };
    check();
  }, [studentId, code, myName, myEmoji]);

  useSessionSocket(sessionInfo ? code : null, (msg) => {
    if (msg.type === "connected" && msg.session) {
      setTotalStudents(msg.session.students.length);
      setDrawMode(msg.session.draw_mode || "numbers");
      setGroupNames(msg.session.group_names || []);
      setDeadline(msg.session.submissions_deadline || null);
      if (studentId) {
        const me = msg.session.students.find((s) => s.id === studentId);
        if (me && (me.number != null || me.group != null)) {
          setMyNumber(me.number);
          setMyGroup(me.group);
          setState("revealed");
          savePersist({
            code, studentId, name: myName, emoji: myEmoji,
            number: me.number, group: me.group, groupNames: msg.session.group_names || [],
          });
        }
      }
    }
    if (msg.type === "student_joined") setTotalStudents((n) => n + 1);
    if (msg.type === "status" && msg.status === "closed" && state === "form") setState("closed");
    if (msg.type === "timer_started") setDeadline(msg.submissions_deadline || null);
    if (msg.type === "timer_stopped" || msg.type === "timer_expired") setDeadline(null);
    if (msg.type === "revealed" && msg.session) {
      setDrawMode(msg.session.draw_mode);
      setGroupNames(msg.session.group_names || []);
      const me = studentId ? msg.session.students.find((s) => s.id === studentId) : null;
      if (me && (me.number != null || me.group != null)) {
        setMyNumber(me.number);
        setMyGroup(me.group);
        setState("revealed");
        savePersist({
          code, studentId, name: myName, emoji: myEmoji,
          number: me.number, group: me.group, groupNames: msg.session.group_names || [],
        });
      }
    }
    if (msg.type === "session_reset") {
      clearPersist();
      setStudentId(null);
      setMyNumber(null);
      setMyGroup(null);
      setState("form");
      setName("");
      fetchSession(code);
    }
  });

  useEffect(() => {
    if (state === "revealed") {
      const end = Date.now() + 900;
      const colors = ["#4F46E5", "#10B981", "#F59E0B", "#F43F5E"];
      (function frame() {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
    }
  }, [state]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code) return toast.error("Enter a class code");
    if (!name.trim()) return toast.error("Enter your name");
    setLoading(true);
    try {
      const { data } = await api.post(`/public/session/${code}/join`, {
        name: name.trim(),
        emoji,
      });
      setStudentId(data.student_id);
      setMyName(name.trim());
      setMyEmoji(emoji);
      setState("waiting");
      savePersist({ code, studentId: data.student_id, name: name.trim(), emoji, number: null, group: null });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const leave = () => {
    clearPersist();
    setStudentId(null);
    setMyNumber(null);
    setMyGroup(null);
    setState("form");
    setName("");
  };

  const groupLabel = myGroup != null ? (groupNames[myGroup - 1] || `Group ${myGroup}`) : "";

  return (
    <div className="min-h-screen paper-bg flex flex-col">
      <div className="max-w-md w-full mx-auto px-4 py-6 flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center rotate-3">
          <Dice5 className="w-4 h-4 text-white" />
        </div>
        <div className="font-display font-extrabold text-slate-900">Random Roster</div>
      </div>

      <div className="flex-1 max-w-md w-full mx-auto px-4 pb-10 flex flex-col justify-center">
        <AnimatePresence mode="wait">
          {state === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="card-paper p-6 sm:p-8 relative"
            >
              <div className="tape left-8 -top-3" />
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600 mb-2">
                {sessionInfo ? `${sessionInfo.teacher_name}'s class` : "Join a class"}
              </div>
              <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-1">
                Enter your name
              </h1>
              <p className="text-sm text-slate-600 mb-6">
                No account, no download. Pick an emoji, type your name, tap Join.
              </p>
              <form onSubmit={submit} className="space-y-4">
                {!codeFromUrl && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Class code
                    </label>
                    <input
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      onBlur={() => fetchSession(code)}
                      data-testid="student-code-input"
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-white focus:border-indigo-500 focus:outline-none text-slate-900 font-mono-num tracking-widest uppercase text-lg"
                      placeholder="ABC123"
                      maxLength={8}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Pick your emoji
                  </label>
                  <div
                    className="grid grid-cols-8 gap-1.5 bg-slate-50 border-2 border-slate-200 rounded-xl p-2"
                    data-testid="student-emoji-picker"
                  >
                    {EMOJI_CHOICES.map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => setEmoji(e)}
                        data-testid={`emoji-choice-${e}`}
                        className={`text-2xl leading-none py-1.5 rounded-lg transition-all ${
                          emoji === e
                            ? "bg-indigo-600 ring-2 ring-indigo-400 scale-110"
                            : "hover:bg-white"
                        }`}
                        aria-label={`emoji ${e}`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Your name
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoFocus
                    data-testid="student-name-input"
                    className="w-full px-4 py-4 rounded-xl border-2 border-slate-200 bg-white focus:border-indigo-500 focus:outline-none text-slate-900 text-lg"
                    placeholder="First name"
                    maxLength={60}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="student-join-submit-button"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2 text-lg py-4"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span className="text-2xl leading-none">{emoji}</span> Join class
                </button>
              </form>
              {countdown.active && (
                <div
                  data-testid="student-timer-form"
                  className={`mt-4 flex items-center justify-center gap-2 text-sm font-bold rounded-xl border-2 py-2 px-3 ${
                    countdown.remainingMs < 10000
                      ? "border-rose-200 bg-rose-50 text-rose-700"
                      : "border-amber-200 bg-amber-50 text-amber-800"
                  }`}
                >
                  <Timer className="w-4 h-4" />
                  Submissions close in <span className="font-mono-num">{countdown.label}</span>
                </div>
              )}
              {sessionInfo && (
                <div className="mt-4 text-xs text-slate-500 text-center">
                  {sessionInfo.student_count} classmate
                  {sessionInfo.student_count === 1 ? "" : "s"} already joined
                </div>
              )}
            </motion.div>
          )}

          {state === "closed" && (
            <motion.div
              key="closed"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-paper p-8 text-center"
            >
              <LockIcon className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h1 className="font-display text-2xl font-extrabold text-slate-900">
                Submissions are closed
              </h1>
              <p className="text-slate-600 mt-2">
                Your teacher has locked the roster. Please wait for the next class.
              </p>
            </motion.div>
          )}

          {state === "waiting" && (
            <motion.div
              key="waiting"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-paper p-8 text-center relative"
              data-testid="student-waiting-indicator"
            >
              <div className="tape left-1/2 -translate-x-1/2 -top-3" />
              <motion.div
                animate={{ rotate: [0, -8, 8, 0] }}
                transition={{ repeat: Infinity, duration: 2.4 }}
                className="w-16 h-16 rounded-2xl bg-indigo-100 mx-auto flex items-center justify-center mb-5 text-4xl"
              >
                {myEmoji || <Hourglass className="w-8 h-8 text-indigo-600" />}
              </motion.div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600 mb-2">
                Submitted
              </div>
              <h1 className="font-display text-3xl font-extrabold text-slate-900">
                Hi, {myName}!
              </h1>
              <p className="text-slate-600 mt-2 leading-relaxed">
                You&apos;re in. Waiting for your teacher to draw…
              </p>
              {countdown.active && (
                <div
                  data-testid="student-timer-waiting"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-bold text-amber-800"
                >
                  <Timer className="w-3.5 h-3.5" />
                  Joining closes in <span className="font-mono-num">{countdown.label}</span>
                </div>
              )}
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Live connection · {totalStudents} joined
              </div>
              <button
                onClick={leave}
                className="mt-6 text-xs text-slate-400 hover:text-slate-700 underline"
                data-testid="student-leave-button"
              >
                Not you? Leave and rejoin
              </button>
            </motion.div>
          )}

          {state === "revealed" && drawMode === "numbers" && (
            <motion.div
              key="reveal-num"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-paper p-8 text-center relative overflow-hidden"
              data-testid="student-revealed-number-card"
            >
              <div className="tape left-1/2 -translate-x-1/2 -top-3" />
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600 mb-2">
                Your number
              </div>
              <div className="text-sm text-slate-600 mb-4 flex items-center justify-center gap-2">
                {myEmoji && <span className="text-xl">{myEmoji}</span>}
                Hi, {myName}!
              </div>
              <motion.div
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                data-testid="student-revealed-number"
                className="font-mono-num text-8xl sm:text-9xl font-black text-indigo-600 leading-none drop-shadow-sm"
              >
                {String(myNumber).padStart(2, "0")}
              </motion.div>
              <p className="mt-6 text-slate-600">
                Keep this screen open. Your teacher will call it out.
              </p>
              <button onClick={leave} className="mt-6 text-xs text-slate-400 hover:text-slate-700 underline">
                Done
              </button>
            </motion.div>
          )}

          {state === "revealed" && drawMode === "groups" && (
            <motion.div
              key="reveal-group"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-paper p-8 text-center relative overflow-hidden"
              data-testid="student-revealed-group-card"
            >
              <div className="tape left-1/2 -translate-x-1/2 -top-3" />
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600 mb-2">
                Your group
              </div>
              <div className="text-sm text-slate-600 mb-4 flex items-center justify-center gap-2">
                {myEmoji && <span className="text-xl">{myEmoji}</span>}
                Hi, {myName}!
              </div>
              <motion.div
                initial={{ scale: 0, rotate: -6 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 240, damping: 18 }}
                className="mx-auto max-w-xs"
              >
                <div className="inline-flex items-center justify-center rounded-2xl bg-indigo-600 text-white px-6 py-4">
                  <UsersIcon className="w-6 h-6 mr-2" />
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-widest text-indigo-200">
                      Group #{myGroup}
                    </div>
                    <div
                      data-testid="student-revealed-group-name"
                      className="font-display text-3xl font-extrabold leading-tight"
                    >
                      {groupLabel}
                    </div>
                  </div>
                </div>
              </motion.div>
              <p className="mt-6 text-slate-600">
                Find your teammates. Your teacher will call groups out.
              </p>
              <button onClick={leave} className="mt-6 text-xs text-slate-400 hover:text-slate-700 underline">
                Done
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {!sessionInfo && code && state === "form" && (
          <div className="mt-4 text-center text-xs text-rose-600">
            Class code &quot;{code}&quot; not found. Double-check with your teacher.
          </div>
        )}
      </div>
    </div>
  );
}
